import { useState } from 'react'
import {
  Box, Button, Checkbox, Grid, GridItem, Heading, Input, NativeSelect,
  Stack, VStack, HStack, Text, Field,
} from '@chakra-ui/react'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { Card } from '../shared/Card'
import { SheetSelect } from '../shared/SheetSelect'
import { useIsMobile } from '../../hooks/useIsMobile'
import type { ContactSearchRequest, ContactSearchOptions } from '../../types/contact'
import { SectorFilter } from './SectorFilter'
import { DistrictFilter, type DistrictFilterValue } from './DistrictFilter'
import { UnionOnly } from '../scoping/UnionOnly'

interface ContactSearchFormProps {
  options?: ContactSearchOptions
  onSearch: (data: ContactSearchRequest) => void
  onClear: () => void
  isLoading?: boolean
}

// Single column on phone (base '1fr'); the original desktop grids at md+.
const rowCols = (md: number) => ({ base: '1fr', md: `repeat(${md}, 1fr)` })
const labelProps = { fontSize: 'sm', color: 'muted' } as const

export function ContactSearchForm({ options, onSearch, onClear, isLoading }: ContactSearchFormProps) {
  const isMobile = useIsMobile()
  const [showMore, setShowMore] = useState(false)
  const { register, handleSubmit, reset, control, setValue } = useForm<ContactSearchRequest>({
    defaultValues: { useSmartNameSearch: true, excludeUnregisteredVoters: false, page: 1, pageSize: 25 },
  })

  const handleClear = () => { reset(); onClear() }

  // District filter spans three request fields; bind them together via watch + setValue rather
  // than a single Controller (one component, three fields).
  const [watchedHouse, watchedSenate, watchedCongress] = useWatch({
    control,
    name: ['stateHouseDistricts', 'stateSenateDistricts', 'congressionalDistricts'],
  })
  const districtOptions = {
    stateHouseDistricts: options?.stateHouseDistricts ?? [],
    stateSenateDistricts: options?.stateSenateDistricts ?? [],
    congressionalDistricts: options?.congressionalDistricts ?? [],
  }
  const districtValue: DistrictFilterValue = {
    stateHouseDistricts: watchedHouse ?? [],
    stateSenateDistricts: watchedSenate ?? [],
    congressionalDistricts: watchedCongress ?? [],
  }
  const setDistrictValue = (next: DistrictFilterValue) => {
    setValue('stateHouseDistricts', next.stateHouseDistricts)
    setValue('stateSenateDistricts', next.stateSenateDistricts)
    setValue('congressionalDistricts', next.congressionalDistricts)
  }

  // On desktop every field shows; on phone the secondary groups collapse behind
  // "More filters". Primary set = Last/First + the Search/Clear actions.
  const showSecondary = !isMobile || showMore

  const countyOptions = (options?.counties ?? []).map((c) => ({ value: c, label: c }))

  // Each name-field GridItem is defined once and placed per viewport below —
  // desktop keeps the original single 6-across row (spec §4: unchanged), mobile
  // shows only Last/First up front with the rest behind "More filters".
  const nameFields = {
    last: (
      <GridItem key="last">
        <Field.Root>
          <Field.Label {...labelProps}>Last Name</Field.Label>
          <Input {...register('lastName')} size="sm" />
        </Field.Root>
      </GridItem>
    ),
    first: (
      <GridItem key="first">
        <Field.Root>
          <Field.Label {...labelProps}>First Name</Field.Label>
          <Input {...register('firstName')} size="sm" />
        </Field.Root>
      </GridItem>
    ),
    middle: (
      <GridItem key="middle">
        <Field.Root>
          <Field.Label {...labelProps}>Middle Name</Field.Label>
          <Input {...register('middleName')} size="sm" />
        </Field.Root>
      </GridItem>
    ),
    nick: (
      <GridItem key="nick">
        <Field.Root>
          <Field.Label {...labelProps}>Nickname</Field.Label>
          <Input {...register('nickName')} size="sm" />
        </Field.Root>
      </GridItem>
    ),
    dob: (
      <GridItem key="dob">
        <Field.Root>
          <Field.Label {...labelProps}>DOB</Field.Label>
          <Input type="date" {...register('dateOfBirth')} size="sm" />
        </Field.Root>
      </GridItem>
    ),
    voterId: (
      <GridItem key="voterId">
        <Field.Root>
          <Field.Label {...labelProps}>Voter File ID</Field.Label>
          <Input {...register('voterFileId')} size="sm" />
        </Field.Root>
      </GridItem>
    ),
  }

  return (
    <Card>
      <form onSubmit={handleSubmit(onSearch)}>
        <VStack gap={6} align="stretch">
          <Box borderBottomWidth="1px" borderColor="border" pb={2}>
            <Text fontSize="xs" color="muted" textTransform="uppercase" letterSpacing="wide">Contacts</Text>
            <Heading size="lg">Quick Look Up</Heading>
          </Box>

          {!isMobile ? (
            // DESKTOP: original single 6-across name row — spec §4 unchanged
            <Grid templateColumns={rowCols(6)} gap={4}>
              {nameFields.last}{nameFields.first}{nameFields.middle}{nameFields.nick}{nameFields.dob}{nameFields.voterId}
            </Grid>
          ) : (
            // MOBILE: primary Last/First, then More-filters discloses the rest
            <>
              <Grid templateColumns={rowCols(6)} gap={4}>{nameFields.last}{nameFields.first}</Grid>
              <Button variant="link" alignSelf="flex-start" onClick={() => setShowMore((v) => !v)}>
                {showMore ? 'Fewer filters' : 'More filters'}
              </Button>
              {showMore && (
                <Grid templateColumns={rowCols(4)} gap={4}>
                  {nameFields.middle}{nameFields.nick}{nameFields.dob}{nameFields.voterId}
                </Grid>
              )}
            </>
          )}

          {showSecondary && (
            <>
              {/* Address */}
              <Grid templateColumns={rowCols(6)} gap={4}>
                <GridItem>
                  <Field.Root>
                    <Field.Label {...labelProps}>Street Address</Field.Label>
                    <Input {...register('streetAddress')} size="sm" />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <Field.Root>
                    <Field.Label {...labelProps}>City</Field.Label>
                    <Input {...register('city')} size="sm" />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <Field.Root>
                    <Field.Label {...labelProps}>State</Field.Label>
                    <Input {...register('state')} size="sm" placeholder="VA" maxLength={2} textTransform="uppercase" />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <Field.Root>
                    <Field.Label {...labelProps}>Zip</Field.Label>
                    <Input {...register('zip')} size="sm" />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <Field.Root>
                    <Field.Label {...labelProps}>Address Type</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field {...register('addressType')} placeholder="">
                        <option value="">Any</option>
                        {options?.addressTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <Field.Root>
                    <Field.Label {...labelProps}>County</Field.Label>
                    <Controller
                      name="county"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <SheetSelect
                          aria-label="County"
                          placeholder="Any"
                          searchable
                          size="sm"
                          options={countyOptions}
                          value={value ?? ''}
                          onChange={onChange}
                        />
                      )}
                    />
                  </Field.Root>
                </GridItem>
              </Grid>

              {/* Employer sector */}
              {(options?.employerTypes?.length ?? 0) > 0 && (
                <Controller
                  control={control}
                  name="employerTypes"
                  render={({ field }) => (
                    <SectorFilter
                      options={options?.employerTypes ?? []}
                      value={field.value ?? []}
                      onChange={field.onChange}
                    />
                  )}
                />
              )}

              {/* District (self-hides when the committee has no districts) */}
              <DistrictFilter
                options={districtOptions}
                value={districtValue}
                onChange={setDistrictValue}
              />

              {/* Contact & employment */}
              <Grid templateColumns={rowCols(5)} gap={4}>
                <GridItem>
                  <Field.Root>
                    <Field.Label {...labelProps}>Email</Field.Label>
                    <Input type="email" {...register('email')} size="sm" />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <Field.Root>
                    <Field.Label {...labelProps}>Phone</Field.Label>
                    <Input {...register('phone')} size="sm" />
                  </Field.Root>
                </GridItem>
                <UnionOnly>
                  <GridItem>
                    <Field.Root>
                      <Field.Label {...labelProps}>Employer</Field.Label>
                      <Input {...register('employerName')} size="sm" placeholder="Business name" />
                    </Field.Root>
                  </GridItem>
                  <GridItem>
                    <Field.Root>
                      <Field.Label {...labelProps}>Union</Field.Label>
                      <Input {...register('unionName')} size="sm" placeholder="Union name" />
                    </Field.Root>
                  </GridItem>
                </UnionOnly>
              </Grid>
            </>
          )}

          {/* Options + actions */}
          <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={4} pt={2}>
            {showSecondary && (
              <Stack direction={{ base: 'column', md: 'row' }} gap={{ base: 3, md: 6 }} align="start">
                <Controller
                  name="useSmartNameSearch"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Checkbox.Root onCheckedChange={(d) => onChange(d.checked === true)} size="sm" checked={value}>
                      <Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                      <Checkbox.Label>Use SmartName search (fuzzy name matching)</Checkbox.Label>
                    </Checkbox.Root>
                  )}
                />
                <Controller
                  name="excludeUnregisteredVoters"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Checkbox.Root onCheckedChange={(d) => onChange(d.checked === true)} size="sm" checked={value}>
                      <Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                      <Checkbox.Label>Exclude Unregistered Voters</Checkbox.Label>
                    </Checkbox.Root>
                  )}
                />
              </Stack>
            )}

            <HStack gap={3} justify={{ base: 'flex-end', md: 'flex-start' }}>
              <Button variant="outline" size="sm" onClick={handleClear}>Clear</Button>
              <Button type="submit" colorPalette="brand" size="sm" loading={isLoading}>Search</Button>
            </HStack>
          </Stack>
        </VStack>
      </form>
    </Card>
  )
}
